import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
} from '@mui/material';

const TermsOfService: React.FC = () => {
  const theme = useTheme();
  const lastUpdated = 'February 17, 2026';

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Terms of Service
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Last updated: {lastUpdated}
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: `1px solid ${theme.palette.divider}` }}>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These Terms of Service ("Terms") govern your access to and use of the Tier One Content platform
            (the "Service"), operated by Jakatech LLC, a Texas limited liability company, doing business as
            Tier One Content ("Jakatech," "we," "us," or "our").
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By creating an account, accessing, or using the Service, you agree to be bound by these Terms.
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you are using the Service on behalf of an organization, you represent that you have authority
            to bind that organization to these Terms.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            1. Definitions
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Service</strong> – The Tier One Content platform, including associated websites, applications, APIs, and features.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>User</strong> – Any individual accessing or using the Service.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Organization</strong> – A company, entity, or group that subscribes to the Service.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Teams</strong> – Subgroups within an Organization.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>User Content</strong> – Any content uploaded, shared, stored, or transmitted through the Service.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Customer Data</strong> – User Content and associated metadata belonging to an Organization.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            2. Eligibility
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You must be at least 16 years old, or the minimum age required under applicable law, to use the Service.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You agree to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Provide accurate account information
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Maintain the security of your account credentials
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Notify us promptly of unauthorized use
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You are responsible for activity occurring under your account.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            3. The Service
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We provide a hosted platform for storing, organizing, sharing, and discovering content within
            and across Organizations and Teams.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Improve, modify, or update the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Add or remove features
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Perform maintenance or downtime as needed
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not guarantee uninterrupted availability.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            4. User Content
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4.1 Ownership
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You retain ownership of all User Content.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By uploading content, you grant Jakatech a limited, non-exclusive, worldwide license to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Host
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Store
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Process
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Display
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Index
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Transmit
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            such content solely for the purpose of operating, maintaining, and improving the Service.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not claim ownership of your content.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4.2 Lawful Upload & Data Protection Compliance
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You represent and warrant that:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You own or have obtained all necessary rights and permissions to upload and share your content.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You have a lawful basis under applicable law (including GDPR, CCPA, and similar regulations) to process and share any personal data contained in your content.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You will not upload unlawfully obtained data, scraped personal databases, or sensitive personal data (including medical records, biometric data, government ID numbers, financial account information, or similar highly sensitive information) without proper legal authorization.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You comply with all applicable privacy, data protection, intellectual property, and confidentiality laws.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You remain solely responsible for your uploaded content.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4.3 Data Protection Roles
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            For personal data contained in uploaded content:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                The Organization or User uploading the content acts as the Data Controller (or "Business" under CCPA).
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Jakatech acts as a Data Processor (or "Service Provider") and processes such data only in accordance with these Terms and documented instructions.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            For account registration and platform administration data, Jakatech acts as an independent Data Controller.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If applicable, our Data Processing Agreement ("DPA") is incorporated by reference and available at: /legal/dpa
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4.4 Automated Processing
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service may use automated systems, including artificial intelligence tools, to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Generate tags
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Create summaries
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Index content
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Improve search functionality
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Provide recommendations
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These processes operate solely to enhance Service functionality.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4.5 Content Removal
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may remove or restrict content that:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Violates these Terms
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Violates applicable law
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Is subject to a valid legal complaint
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Poses legal, security, or reputational risk
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We are not obligated to monitor content proactively.
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            4.6 Indemnification
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You agree to indemnify, defend, and hold harmless Jakatech LLC, its officers, directors, employees,
            and agents from any claims, damages, liabilities, fines, penalties, and expenses (including reasonable
            attorneys' fees) arising from:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Your uploaded content
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Your violation of these Terms
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Your violation of data protection or intellectual property laws
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            5. Acceptable Use
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You agree not to:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Upload illegal, infringing, abusive, or harmful content
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Attempt to hack, disrupt, or overload the Service
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Interfere with security features
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Use the Service to send spam or unauthorized communications
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            6. Subscriptions & Payment
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Certain features require a paid subscription.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Subscription fees, billing cycles, and cancellation terms are governed by our Subscription & Billing
            Terms, available at: /legal/billing
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Failure to pay may result in suspension or termination.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            7. Termination
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may stop using the Service at any time.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may suspend or terminate access if:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                You violate these Terms
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Required by law
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Necessary to protect the Service or other users
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Upon termination:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Organizations may request export of Customer Data within 30 days.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                After that period, we may securely delete data in accordance with our retention policies.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            8. Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service, including software, branding, and underlying technology, is owned by Jakatech LLC.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You may not:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Reverse engineer
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Copy
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Modify
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Resell
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Exploit
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            any portion of the Service without written permission.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            9. Confidentiality
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We will treat Customer Data as confidential and use reasonable measures to protect it.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You agree not to misuse confidential information belonging to other users or Organizations.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            10. Security & Data Breach Notification
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We implement commercially reasonable security measures including:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Encryption in transit
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Encryption at rest
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Access controls
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Audit logging
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            In the event of a confirmed personal data breach affecting Customer Data, we will notify the affected
            Organization without undue delay, consistent with applicable law.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            11. Disclaimers
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service is provided "as is" and "as available."
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            To the fullest extent permitted by law, we disclaim all warranties, express or implied, including:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Merchantability
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Fitness for a particular purpose
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Non-infringement
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not guarantee uninterrupted or error-free service.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            12. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            To the maximum extent permitted by law:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Our total liability for claims arising from the Service will not exceed the fees paid by you in the 12 months preceding the claim.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                We are not liable for indirect, incidental, consequential, or punitive damages.
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Nothing in these Terms limits liability where prohibited by law.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            13. Governing Law & Disputes
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These Terms are governed by the laws of the State of Texas, United States.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Disputes will be resolved in the courts located in Dallas County, Texas.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Nothing in this section limits mandatory consumer protection rights under applicable law.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            14. Jury Trial Waiver
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            To the fullest extent permitted by applicable law, you and Jakatech LLC knowingly, voluntarily,
            and irrevocably waive any right to a trial by jury in any legal proceeding arising out of or relating
            to these Terms, the Service, or any dispute between the parties. Each party further waives any objection
            to venue based on inconvenience.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Each party acknowledges that:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                This waiver is a material inducement for entering into these Terms.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                It has had the opportunity to consult with legal counsel.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                It understands that, absent this waiver, it would have the right to a trial by jury.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                This waiver applies to all claims, whether based in contract, tort, statute, or otherwise.
              </Typography>
            </li>
          </ul>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            15. Force Majeure
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We are not liable for delays or failure to perform resulting from causes beyond our reasonable control,
            including natural disasters, internet outages, government actions, or third-party service failures.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            16. Changes to These Terms
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may update these Terms from time to time.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If changes are material, we will provide notice via one or more of the following:
          </Typography>
          <ul>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Email
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Platform notification
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Updated "Last Updated" date
              </Typography>
            </li>
          </ul>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Continued use after updates constitutes acceptance.
          </Typography>

          <Typography variant="h4" sx={{ mt: 5, mb: 3, fontWeight: 600 }}>
            17. Contact Information
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Jakatech LLC<br />
            d/b/a Tier One Content<br />
            10228 E Northwest Hwy #1001<br />
            Dallas, TX 75238<br />
            Email: support@tieronecontent.com
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Typography variant="body2" color="text.secondary">
            By using Tier One Content, you acknowledge that you have read and agree to these Terms.
            Last updated: {lastUpdated}.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default TermsOfService;
